<template>
    <div class="billing-rate-manager">
        <div class="card">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
                <strong>Billing Rates by Department</strong>
                <button 
                    v-if="!isEditing"
                    type="button" 
                    class="btn btn-sm btn-outline-primary"
                    @click="startEditing"
                >
                    <Edit :size="14" class="me-1" />
                    Edit Rates
                </button>
            </div>
            <div class="card-body p-0">
                <div v-if="loading" class="text-center py-3">
                    <span class="spinner-border spinner-border-sm"></span>
                    Loading departments...
                </div>
                <div v-else-if="departments.length === 0" class="text-center py-3 text-muted">
                    No departments configured. Add departments in Request settings.
                </div>
                <table v-else class="table table-sm table-hover mb-0">
                    <thead>
                        <tr>
                            <th>Department</th>
                            <th style="width: 200px">Billing Rate ($/hr)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="dept in departments" :key="dept.name">
                            <td>{{ dept.name }}</td>
                            <td>
                                <div v-if="isEditing" class="input-group input-group-sm">
                                    <span class="input-group-text">$</span>
                                    <input 
                                        type="number" 
                                        class="form-control form-control-sm"
                                        v-model.number="rateMap[dept.name]"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div v-else>
                                    <span v-if="rateMap[dept.name]">
                                        ${{ Number(rateMap[dept.name]).toFixed(2) }}/hr
                                    </span>
                                    <span v-else class="text-muted">Not set</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="isEditing" class="card-footer py-2">
                <div class="d-flex justify-content-end gap-2">
                    <button 
                        type="button" 
                        class="btn btn-sm btn-outline-secondary"
                        @click="cancelEditing"
                    >
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        class="btn btn-sm btn-primary"
                        @click="saveRates"
                    >
                        <Save :size="14" class="me-1" />
                        Save Rates
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import OptionsService from '@/services/OptionsService';
import Toast from '@/services/Toast';
import { Edit, Save } from 'lucide-vue-next';

interface Department {
    name: string;
    email: string;
}

const props = defineProps<{
    refKey: string;  // e.g., 'Contact' or 'Technician'
    refVal: string;  // e.g., CustomerNumber or TechnicianId
}>();

const emit = defineEmits<{
    (e: 'rates-changed', rates: Record<string, number>): void;
}>();

const loading = ref(false);
const isEditing = ref(false);
const departments = ref<Department[]>([]);
const rateMap = ref<Record<string, number>>({});
const originalRateMap = ref<Record<string, number>>({});

const DEPARTMENT_OPTION_KEY = 'departments_json';

const loadDepartments = async () => {
    loading.value = true;
    try {
        const optionValue = await OptionsService.get(DEPARTMENT_OPTION_KEY);
        if (optionValue) {
            departments.value = JSON.parse(optionValue);
        } else {
            departments.value = [];
        }
    } catch (error) {
        console.error('Error loading departments:', error);
        departments.value = [];
    } finally {
        loading.value = false;
    }
};

const loadRates = async () => {
    if (!props.refVal) return;
    
    try {
        const optionKey = `billing_rates_${props.refKey.toLowerCase()}`;
        const savedRates = await OptionsService.get(optionKey, props.refKey, props.refVal);
        
        if (savedRates) {
            rateMap.value = JSON.parse(savedRates);
        } else {
            rateMap.value = {};
        }
    } catch (error) {
        console.error('Error loading billing rates:', error);
        rateMap.value = {};
    }
};

const startEditing = () => {
    originalRateMap.value = { ...rateMap.value };
    isEditing.value = true;
};

const cancelEditing = () => {
    rateMap.value = { ...originalRateMap.value };
    isEditing.value = false;
};

const saveRates = async () => {
    if (!props.refVal) {
        Toast.error('Cannot save rates: record not yet saved');
        return;
    }

    try {
        const optionKey = `billing_rates_${props.refKey.toLowerCase()}`;
        await OptionsService.set(optionKey, JSON.stringify(rateMap.value), props.refKey, props.refVal);
        
        Toast.success('Billing rates saved successfully');
        isEditing.value = false;
        emit('rates-changed', rateMap.value);
    } catch (error) {
        console.error('Error saving billing rates:', error);
        Toast.error('Failed to save billing rates');
    }
};

watch(() => props.refVal, async (newVal) => {
    if (newVal) {
        await loadRates();
    }
}, { immediate: true });

onMounted(async () => {
    await loadDepartments();
    if (props.refVal) {
        await loadRates();
    }
});

defineExpose({
    getRateForDepartment: (departmentName: string): number | null => {
        return rateMap.value[departmentName] ?? null;
    },
    getAllRates: () => rateMap.value
});
</script>

<style scoped>
.billing-rate-manager {
    margin-top: 1rem;
}
</style>
